/**
 * reviewStore — module-scope state for the per-section regulatory review.
 *
 * The review control (in ``DataGapsTabPanel.tsx``, the unified Issues tab)
 * unmounts whenever the inspector closes or the focused section changes, but the audit
 * it started is a ~1-2 min server request. Holding the run state here — keyed
 * by ``${runId}:${moduleId}``, outside React — means:
 *
 *   - closing/reopening the inspector re-attaches to the in-flight run
 *     (the awaited POST writes to the store, not to component state, so its
 *     completion lands even while nothing is mounted — and the live-doc
 *     re-projection still runs);
 *   - a full add-in reload recovers via the server's ``audit_status`` field
 *     on GET /regulator-flags (Redis-backed, cross-worker) plus the run's
 *     ``audit_complete`` / ``audit_error`` SSE events (forwarded from
 *     useVariantCRun) and a slow poll fallback;
 *   - cross-section display is structurally impossible: every write lands
 *     under the key of the section that initiated it, and every server
 *     response is discarded (with a console.warn) unless its ``module_id``
 *     matches the key it was requested for.
 *
 * SRP: state + transitions only. Rendering stays in DataGapsTabPanel; the
 * component subscribes via useSyncExternalStore(subscribe, () => getEntry(key)).
 */

import {
	auditSection,
	getRegulatorFlags,
	getRunSnapshot,
	type RegulatorFlagsResponse,
	type RegulatoryFinding,
	syncCustomSection,
} from "../api/client";
import { readCustomSpineOutline, stripRegulatorFlagsInSection } from "../services/wordWriter";
import { reinsertModuleIntoLiveDoc } from "./services/reinsertModuleIntoLiveDoc";
import { stripRegulatorMarkers } from "./services/draftSanitizer";
import { deferUntilLiveDocSettled, isLiveDocLoading } from "../services/liveDocLoadState";

export type ReviewRunState = "idle" | "running" | "done" | "error";

export interface ReviewEntry {
	runState: ReviewRunState;
	findings: RegulatoryFinding[];
	flagCount: number;
	evidence: string;
	statusNote: string;
	error: string | null;
	/** Epoch ms when the (local or attached) run started; drives staleness. */
	startedAt: number | null;
	hasRun: boolean;
	/** True once getRegulatorFlags has populated this entry at least once. */
	hydrated: boolean;
	/** True while the auditSection POST is in flight in THIS JS context —
	 *  its resolution is then authoritative and SSE/poll updates stand down. */
	ownedByThisClient: boolean;
	/** Rolling narration line shown while running (e.g. "Searching precedent: …").
	 *  Display-only — fed by audit_progress/phase SSE events; null when idle/done. */
	progress: string | null;
	/** Custom-submission only: the writer's REAL heading number for this module
	 *  (the module id is the synthetic ``CUSTOM-N``). Set by ``startAudit`` so
	 *  every re-projection path (owned completion, SSE, attached poll) can write
	 *  the audit markers at the writer's actual heading instead of a no-op.
	 *  Undefined for normal template runs. */
	customSectionNumber?: string;
	/** Custom-submission only: the writer's heading TITLE. A custom heading renders
	 *  its title with no leading number and no bookmark, so the title (not the
	 *  number) is what actually resolves it in the live doc. Captured from the live
	 *  outline in ``startAudit``; threaded into every re-projection. Undefined for
	 *  template runs. */
	customSectionTitle?: string;
}

/** A "running" audit older than this is treated as dead (mirrors the server's
 *  STALE_RUNNING_AFTER_SECONDS / status-key TTL of 600s). */
const STALE_RUNNING_AFTER_MS = 10 * 60 * 1000;

/** Poll cadence while attached to a run we don't own (reloaded add-in with no
 *  SSE yet, or a second client). Slow on purpose — SSE is the fast path. */
const ATTACHED_POLL_INTERVAL_MS = 7_000;

const EMPTY_ENTRY: ReviewEntry = Object.freeze({
	runState: "idle",
	findings: [],
	flagCount: 0,
	evidence: "",
	statusNote: "",
	error: null,
	startedAt: null,
	hasRun: false,
	hydrated: false,
	ownedByThisClient: false,
	progress: null,
});

const entries = new Map<string, ReviewEntry>();
const listeners = new Map<string, Set<() => void>>();
const pollTimers = new Map<string, ReturnType<typeof setInterval>>();

// Global (run-scoped) "is any section audit in flight" signal, separate from the
// per-key listeners. The shell subscribes to this to keep an SSE stream open for
// the run while a manual audit runs — even on an idle/completed run, which
// otherwise has no live stream and would show none of the audit's step progress.
const auditActivityListeners = new Set<() => void>();

export function subscribeAuditActivity(cb: () => void): () => void {
	auditActivityListeners.add(cb);
	return () => {
		auditActivityListeners.delete(cb);
	};
}

/** True when any section of ``runId`` currently has an audit in the "running" state. */
export function isAuditActiveForRun(runId: string): boolean {
	const prefix = `${runId}:`;
	for (const [key, entry] of entries) {
		if (key.startsWith(prefix) && entry.runState === "running") return true;
	}
	return false;
}

// --- Audit-library build phase (run-scoped, SSE-driven) --------------------
// The audit-worker streams the audit-library build lifecycle as run-scoped
// ``audit_library_*`` SSE events (no ``module_id``): the temp-source bootstrap a
// custom submission needs when it has no source library
// (``audit_library_temp_source`` phases building/waiting/ready/failed/deleting/
// deleted), plus ``audit_library_started`` / ``audit_library_ready``. The
// audit-library panel is otherwise poll-only (6s), so the multi-minute build —
// especially the "build a temp library from your own document" stretch, which
// has no documents to show — reads as a hang. We fan these events into a tiny
// run-keyed store the panel reads for a live status line; the /audit-library
// poll stays the source of truth for status/progress/documents.

export interface AuditLibraryBuildPhase {
	/** Coarse phase: temp-source lifecycle (building/waiting/ready/failed/
	 *  deleting/deleted) or the library-level started/ready. */
	phase: string;
	/** Server-formatted human line, shown verbatim in the panel. */
	message: string | null;
	/** The event type that produced it. */
	eventType: string;
	/** Epoch ms — lets a consumer prefer a fresh SSE line over a stale poll. */
	ts: number;
}

const auditLibraryBuild = new Map<string, AuditLibraryBuildPhase>();
const auditLibraryBuildListeners = new Set<() => void>();

export function subscribeAuditLibraryBuild(cb: () => void): () => void {
	auditLibraryBuildListeners.add(cb);
	return () => {
		auditLibraryBuildListeners.delete(cb);
	};
}

/** Latest build-phase line for ``runId`` (null once cleared / never seen). */
export function getAuditLibraryBuild(runId: string): AuditLibraryBuildPhase | null {
	return auditLibraryBuild.get(runId) ?? null;
}

function emitAuditLibraryBuild(): void {
	for (const cb of auditLibraryBuildListeners) cb();
}

function setAuditLibraryBuildPhase(runId: string, next: AuditLibraryBuildPhase): void {
	auditLibraryBuild.set(runId, next);
	emitAuditLibraryBuild();
}

function clearAuditLibraryBuild(runId: string): void {
	if (auditLibraryBuild.delete(runId)) emitAuditLibraryBuild();
}

export function reviewKey(runId: string, moduleId: string): string {
	return `${runId}:${moduleId}`;
}

/** Snapshot for useSyncExternalStore — stable identity until the next write. */
export function getEntry(key: string): ReviewEntry {
	return entries.get(key) ?? EMPTY_ENTRY;
}

export function subscribe(key: string, cb: () => void): () => void {
	let set = listeners.get(key);
	if (!set) {
		set = new Set();
		listeners.set(key, set);
	}
	set.add(cb);
	return () => {
		set?.delete(cb);
	};
}

function update(key: string, patch: Partial<ReviewEntry>): ReviewEntry {
	const prev = getEntry(key);
	const next = { ...prev, ...patch };
	entries.set(key, next);
	for (const cb of listeners.get(key) ?? []) cb();
	// A running↔not-running transition changes the run-scoped audit-activity
	// signal — wake the shell so it can open/close the audit SSE stream.
	if (patch.runState !== undefined && patch.runState !== prev.runState) {
		for (const cb of auditActivityListeners) cb();
	}
	return next;
}

function stopPoll(key: string): void {
	const timer = pollTimers.get(key);
	if (timer !== undefined) {
		clearInterval(timer);
		pollTimers.delete(key);
	}
}

function parseStartedAt(iso: string | null | undefined): number | null {
	if (!iso) return null;
	const ms = Date.parse(iso);
	return Number.isNaN(ms) ? null : ms;
}

function isStaleRun(startedAt: number | null): boolean {
	return startedAt !== null && Date.now() - startedAt > STALE_RUNNING_AFTER_MS;
}

/** Map a regulator-flags response onto an entry patch (findings portion only).
 *  Mirrors the panel's original mount mapping, incl. the pre-iteration-2
 *  fallback where only marker text survives. */
function findingsPatch(resp: RegulatorFlagsResponse): Partial<ReviewEntry> {
	const findings =
		resp.regulatory_findings && resp.regulatory_findings.length > 0
			? resp.regulatory_findings
			: resp.flags.map((f) => ({
					requirement: f.text.replace(/^\[REGULATOR FLAG:\s*/i, "").replace(/\]$/, ""),
					issue: "",
					resolution: "",
					severity: "",
					status: "open",
				}));
	return {
		findings,
		flagCount: resp.regulator_flag_count,
		evidence: resp.regulatory_evidence_summary ?? "",
		hydrated: true,
	};
}

/** Best-effort live-doc rewrite so the [REGULATOR FLAG: …] markers appear
 *  inline (and become jump-to-able). Non-fatal by design — a no-op when no
 *  document is open or the heading isn't present. */
async function _reprojectNow(
	runId: string,
	moduleId: string,
	options: { stripRegulatorFlags?: boolean; customSectionNumber?: string; customSectionTitle?: string } = {},
): Promise<void> {
	try {
		// Custom runs: the real heading number/title live on the store entry (set by
		// startAudit) so SSE/poll re-projection paths — which pass no options —
		// still write markers at the writer's heading instead of a no-op. The title
		// is what resolves an unnumbered custom heading.
		const entry = getEntry(reviewKey(runId, moduleId));
		const customSectionNumber = options.customSectionNumber ?? entry.customSectionNumber;
		const customSectionTitle = options.customSectionTitle ?? entry.customSectionTitle;
		const { sectionsWritten } = await reinsertModuleIntoLiveDoc(runId, moduleId, {
			...options,
			...(customSectionNumber ? { customSectionNumber } : {}),
			...(customSectionTitle ? { customSectionTitle } : {}),
		});
		if (sectionsWritten === 0) {
			console.warn(
				`[reviewStore] re-projection wrote 0 sections for ${moduleId}; ` +
					"markers may not appear until the run is reloaded into the doc",
			);
		}
	} catch (err) {
		console.warn("[reviewStore] live re-projection failed (non-fatal):", err);
	}
}

/** Gate the live-doc re-projection on the load/settle window. While a run is
 *  being loaded into Word the document is still settling (native numbering +
 *  styles re-attaching); a delete-then-reinsert running now would mis-detect
 *  section boundaries and blank a large region of the live doc — the "blanks a
 *  few seconds after Load" bug. Deferring until the load settles keeps the
 *  markers appearing inline without racing the load. Outside the load window we
 *  still await it, so a subsequent issue click finds the markers rather than
 *  racing an in-flight re-projection. */
async function reprojectSection(
	runId: string,
	moduleId: string,
	options: { stripRegulatorFlags?: boolean; customSectionNumber?: string; customSectionTitle?: string } = {},
): Promise<void> {
	if (isLiveDocLoading()) {
		deferUntilLiveDocSettled(reviewKey(runId, moduleId), () => void _reprojectNow(runId, moduleId, options));
		return;
	}
	await _reprojectNow(runId, moduleId, options);
}

/** Attach to a server-side run this client didn't start: mark running and
 *  poll regulator-flags until the status key clears (SSE usually wins). */
function attachToRemoteRun(key: string, runId: string, moduleId: string, startedAtIso?: string | null): void {
	update(key, {
		runState: "running",
		error: null,
		ownedByThisClient: false,
		startedAt: parseStartedAt(startedAtIso) ?? Date.now(),
		progress: getEntry(key).progress ?? "Running regulatory review…",
	});
	if (pollTimers.has(key)) return;
	pollTimers.set(
		key,
		setInterval(() => {
			void pollAttachedRun(key, runId, moduleId);
		}, ATTACHED_POLL_INTERVAL_MS),
	);
}

async function pollAttachedRun(key: string, runId: string, moduleId: string): Promise<void> {
	const entry = getEntry(key);
	if (entry.runState !== "running" || entry.ownedByThisClient) {
		stopPoll(key);
		return;
	}
	if (isStaleRun(entry.startedAt)) {
		stopPoll(key);
		console.warn(`[reviewStore] attached audit for ${moduleId} exceeded the stale cutoff; returning to idle`);
		update(key, { runState: "idle", ownedByThisClient: false });
		return;
	}
	let resp: RegulatorFlagsResponse;
	try {
		resp = await getRegulatorFlags(runId, moduleId);
	} catch {
		return; // transient — next tick retries
	}
	if (resp.module_id !== moduleId) {
		console.warn(`[reviewStore] module_id mismatch: got ${resp.module_id} want ${moduleId} (poll) — discarded`);
		return;
	}
	if (getEntry(key).ownedByThisClient) return; // a local run took over meanwhile
	if (resp.audit_status === "running") return; // still going
	stopPoll(key);
	if (resp.audit_status === "error") {
		update(key, {
			runState: "error",
			error: "The regulatory audit could not be completed. Try again.",
			ownedByThisClient: false,
		});
		return;
	}
	// Status key cleared → the audit finished and persisted; same response
	// already carries the findings.
	update(key, {
		...findingsPatch(resp),
		runState: "done",
		statusNote: "ok",
		hasRun: true,
		error: null,
		ownedByThisClient: false,
	});
	void reprojectSection(runId, moduleId);
}

/**
 * Populate the entry from the server (panel mount / section focus). Never
 * clobbers a run this client owns; a server-side ``audit_status: "running"``
 * attaches to the remote run instead of showing the idle empty state.
 */
export async function hydrateFromServer(runId: string, moduleId: string): Promise<void> {
	const key = reviewKey(runId, moduleId);
	let resp: RegulatorFlagsResponse;
	try {
		resp = await getRegulatorFlags(runId, moduleId);
	} catch {
		// pre-run / not found — leave the entry as-is, no error surfaced
		return;
	}
	if (resp.module_id !== moduleId) {
		console.warn(`[reviewStore] module_id mismatch: got ${resp.module_id} want ${moduleId} (hydrate) — discarded`);
		return;
	}
	const entry = getEntry(key);
	if (entry.runState === "running" && entry.ownedByThisClient) {
		return; // the local POST is authoritative
	}
	if (resp.audit_status === "running" && !isStaleRun(parseStartedAt(resp.audit_started_at))) {
		update(key, findingsPatch(resp));
		attachToRemoteRun(key, runId, moduleId, resp.audit_started_at);
		return;
	}
	update(key, {
		...findingsPatch(resp),
		// A lingering "error" status means the last audit failed with nothing
		// (new) persisted — surface it instead of a silent empty state.
		...(resp.audit_status === "error" && entry.runState !== "done"
			? {
					runState: "error" as const,
					error: "The last regulatory audit failed. Try again.",
				}
			: {}),
	});
}

/** Custom-submission audit context. A custom run's SSOT (``draft_full``) is
 *  written once at import; ``sectionNumber`` is the writer's REAL heading number
 *  (the module id is the synthetic ``CUSTOM-N``), used to (1) capture the live
 *  section for the pre-audit SSOT sync and (2) target marker re-projection at
 *  the actual heading. Omitted for normal template runs. */
export interface AuditCustomContext {
	sectionNumber: string;
	title?: string;
}

/**
 * Run (or re-run) the audit for a section. All progress lands in the store,
 * so the panel can unmount/remount freely while this is in flight.
 */
export async function startAudit(runId: string, moduleId: string, custom?: AuditCustomContext): Promise<void> {
	const key = reviewKey(runId, moduleId);
	if (getEntry(key).runState === "running") return; // button is disabled, but guard anyway
	stopPoll(key);
	update(key, {
		runState: "running",
		error: null,
		ownedByThisClient: true,
		startedAt: Date.now(),
		progress: "Starting regulatory review…",
	});

	// Custom submission: refresh the persisted SSOT from the writer's CURRENT
	// live content BEFORE auditing. Without this the auditor reads the stale
	// import snapshot, and the marker re-projection below would REVERT the
	// writer's post-import edits. Uses the table-aware outline capture (not the
	// plain-text readSectionSubtree, which would flatten tables) and strips any
	// existing flags so the SSOT is clean prose.
	//
	// ``customSectionNumber``/``customSectionTitle`` on the entry are what enable the
	// body-REPLACING completion re-projection, so they are set together ONLY when
	// that replacement is safe — i.e. after a successful sync of NON-BLANK captured
	// content. The captured ``row.content`` is the section's OWN prose only (never
	// the subtree — see _outlineToCustomSpine), and insertSectionContent's boundary
	// is the next heading of ANY level, so writing it back replaces only that
	// section's own prose and never deletes child sections — PARENT sections are
	// safe too. The one real hazard is a blank/failed capture (we didn't refresh the
	// SSOT, so re-projecting the stale import snapshot would revert edits): guarded
	// by the ``synced`` flag. A custom heading renders its TITLE with no number, so
	// the title (captured LIVE — the writer may have renamed it) is what actually
	// resolves the heading; ``custom.sectionNumber`` from the spine is a gutter
	// number absent from the heading text. Best-effort throughout.
	const isCustom = !!(custom?.sectionNumber || custom?.title);
	let customLiveTitle = custom?.title;
	if (isCustom && !isLiveDocLoading()) {
		let synced = false;
		try {
			const outline = await readCustomSpineOutline();
			const row =
				(custom?.sectionNumber ? outline.find((r) => r.number === custom.sectionNumber) : undefined) ??
				(custom?.title ? outline.find((r) => r.title === custom.title) : undefined);
			if (row?.title) customLiveTitle = row.title;
			const liveContent = row?.content ? stripRegulatorMarkers(row.content).trim() : "";
			if (liveContent) {
				await syncCustomSection(runId, moduleId, liveContent);
				synced = true;
			}
		} catch (err) {
			console.warn("[reviewStore] custom live→SSOT sync failed (non-fatal); auditing last-synced content:", err);
		}
		// Enable the body-REPLACING completion re-projection ONLY when the SSOT was
		// refreshed. Both number + live title are stored so the reproject can resolve
		// the heading by title (the number alone won't match an unnumbered heading).
		if (synced) update(key, { customSectionNumber: custom?.sectionNumber, customSectionTitle: customLiveTitle });
	}

	if (isCustom) {
		// Robust clean slate for custom: delete EVERY [REGULATOR FLAG: …] in the
		// section's live range (prose + table cells) IN PLACE, resolving the heading
		// by title. Independent of the server re-render and of a successful sync, so
		// stale flags are removed even when the capture was blank — and there is no
		// revert risk (it only deletes marker runs, never re-renders content).
		try {
			await stripRegulatorFlagsInSection([{ number: custom?.sectionNumber, title: customLiveTitle }]);
		} catch (err) {
			console.warn("[reviewStore] custom in-place flag strip failed (non-fatal):", err);
		}
	} else {
		// Template runs: the re-render strip is the clean slate (re-renders the
		// stripped server draft; table cells swept by stripRegulatorFlagsInTableCells).
		// Custom is handled by the in-place strip above — re-rendering here would
		// revert the writer's edits when the sync was skipped, so it's skipped.
		await reprojectSection(runId, moduleId, { stripRegulatorFlags: true });
	}
	try {
		// Drug/indication are inferred server-side from the document library.
		const result = await auditSection(runId, moduleId);
		if (result.module_id !== moduleId) {
			console.warn(`[reviewStore] module_id mismatch: got ${result.module_id} want ${moduleId} (audit) — discarded`);
			update(key, { runState: "idle", ownedByThisClient: false });
			// Restore the flags we stripped up front — this run didn't audit.
			void reprojectSection(runId, moduleId);
			void hydrateFromServer(runId, moduleId);
			return;
		}
		if (result.status === "llm_error") {
			update(key, {
				runState: "error",
				statusNote: result.status,
				error: "The regulatory audit could not be completed. Try again.",
				ownedByThisClient: false,
				progress: null,
			});
			// The server draft is unchanged on failure — restore the old flags.
			void reprojectSection(runId, moduleId);
			return;
		}
		update(key, {
			hasRun: true,
			statusNote: result.status,
			flagCount: result.regulator_flag_count,
			evidence: result.evidence_summary ?? "",
			findings: result.requirements ?? [],
			hydrated: true,
		});
		// Rewrite the audited section in the live document so the markers
		// appear inline at the anchored locations. Awaited *before* flipping
		// to "done" so a subsequent issue click finds the markers rather than
		// racing an in-flight re-projection.
		await reprojectSection(runId, moduleId);
		update(key, { runState: "done", error: null, ownedByThisClient: false, progress: null });
	} catch (e) {
		const detail = (e as { detail?: { error?: string; started_at?: string | null } }).detail;
		if (detail && typeof detail === "object" && detail.error === "audit_in_progress") {
			// Another client / an earlier invisible run already has this
			// section — attach to it instead of surfacing an error.
			attachToRemoteRun(key, runId, moduleId, detail.started_at);
			return;
		}
		const notReady = detail as
			| { total?: number; processed?: number; building?: boolean; status?: string }
			| undefined;
		if (detail && typeof detail === "object" && detail.error === "audit_library_not_ready") {
			// The precedent-aware audit library is the ONLY audit (the legacy
			// prompt is retired), and it isn't ready yet. Return to idle (not a
			// hard error) with a clear message. The server auto-starts a build
			// when it can, so the actionable ask is usually just "try again
			// shortly"; the "Run review" button also stays disabled by
			// auditLibraryBlocksAudit until the library is ready.
			const progress =
				typeof notReady?.total === "number" && typeof notReady?.processed === "number" && notReady.total > 0
					? ` (${notReady.processed}/${notReady.total} documents ready)`
					: "";
			const building = notReady?.building ?? (notReady?.status === "acquiring" || notReady?.status === "processing");
			update(key, {
				runState: "idle",
				ownedByThisClient: false,
				error: building
					? `Audit library is still building${progress} — it's being prepared now; try again shortly.`
					: `Audit library isn't ready yet${progress} — build it from the Library Hub, then run the review.`,
			});
			// The audit never ran — restore the flags we stripped up front.
			void reprojectSection(runId, moduleId);
			return;
		}
		update(key, {
			runState: "error",
			error: e instanceof Error ? e.message : "Audit request failed.",
			ownedByThisClient: false,
			progress: null,
		});
		// Restore the flags we stripped up front — the audit didn't persist.
		void reprojectSection(runId, moduleId);
	}
}

/** Human rolling-line message carried on an audit SSE event (server pre-formats
 *  ``message`` on the event and/or its ``section_response`` payload). */
function auditEventMessage(event: {
	message?: string;
	section_response?: Record<string, unknown>;
}): string | null {
	if (typeof event.message === "string" && event.message.trim()) return event.message;
	const payloadMsg = event.section_response?.message;
	if (typeof payloadMsg === "string" && payloadMsg.trim()) return payloadMsg;
	return null;
}

/** Non-terminal audit events that only refresh the rolling narration line. */
const AUDIT_NARRATION_EVENTS = new Set([
	"audit_progress",
	"audit_guidelines_loaded",
	"audit_context_inferred",
	"audit_precedents_fetched",
]);

/**
 * Fold a run-scoped ``audit_library_*`` SSE event into the build-phase store.
 * ``audit_library_ready`` (whole library assembled) clears it so the panel hands
 * back to the now-ready /audit-library poll; every other phase — including a
 * terminal temp-source ``failed`` — stays visible until ``ready`` or the next
 * build overwrites it.
 */
function handleAuditLibrarySseEvent(
	runId: string,
	event: { event_type: string; message?: string; section_response?: Record<string, unknown> },
): void {
	if (event.event_type === "audit_library_ready") {
		clearAuditLibraryBuild(runId);
		return;
	}
	const payload = event.section_response ?? {};
	const phase = typeof payload.phase === "string" ? payload.phase : event.event_type;
	setAuditLibraryBuildPhase(runId, {
		phase,
		message: auditEventMessage(event),
		eventType: event.event_type,
		ts: Date.now(),
	});
}

/**
 * SSE fan-in (wired in useVariantCRun.handleEvent). Terminal events drive the
 * runState/findings; non-terminal ones keep the rolling ``progress`` line moving
 * (display-only — updated even for owned runs, whose findings still come from
 * the authoritative POST). Spine sublines flow separately through the adapter.
 */
export function handleAuditSseEvent(
	runId: string,
	event: { event_type: string; module_id?: string; message?: string; section_response?: Record<string, unknown> },
): void {
	// Run-scoped audit-library build lifecycle (audit_library_temp_source /
	// audit_library_started / audit_library_ready) — no ``module_id``. Route them
	// to the build-phase store so the audit-library panel narrates the build live,
	// then return: these never touch a per-section entry. Handled BEFORE the
	// ``module_id`` guard below, which would otherwise drop them silently.
	if (event.event_type.startsWith("audit_library_")) {
		handleAuditLibrarySseEvent(runId, event);
		return;
	}
	if (!event.module_id) return;
	const moduleId = event.module_id;
	const key = reviewKey(runId, moduleId);
	// A backend-initiated audit (the automatic post-draft review, or another
	// client's manual run) announces itself with audit_started. Reflect it as
	// "Running…" immediately and attach so this panel finishes when
	// audit_complete / the poll arrives — instead of lagging until the next
	// hydrate. Owned runs ignore SSE for state, but still refresh the line.
	if (event.event_type === "audit_started") {
		const started = getEntry(key);
		const msg = auditEventMessage(event);
		if (started.ownedByThisClient || started.runState === "running") {
			if (msg) update(key, { progress: msg });
			return;
		}
		attachToRemoteRun(key, runId, moduleId);
		if (msg) update(key, { progress: msg });
		return;
	}
	// Rolling narration: refresh the line for any tracked run (owned included).
	if (AUDIT_NARRATION_EVENTS.has(event.event_type)) {
		const cur = getEntry(key);
		if (cur.runState === "running" || cur.ownedByThisClient) {
			const msg = auditEventMessage(event);
			if (msg) update(key, { progress: msg });
		}
		return;
	}
	if (event.event_type !== "audit_complete" && event.event_type !== "audit_error") return;
	const entry = getEntry(key);
	if (entry.ownedByThisClient) return;
	if (event.event_type === "audit_error") {
		if (entry.runState === "running") {
			stopPoll(key);
			update(key, {
				runState: "error",
				error: "The regulatory audit could not be completed. Try again.",
				progress: null,
			});
		}
		return;
	}
	// audit_complete — the findings are persisted now; refetch them into the
	// store (also covers panels that never showed "running").
	stopPoll(key);
	const priorRunState = entry.runState;
	// Hold the entry in "running" across the fetch + re-projection below. The
	// live-doc reconcile guards (DataGapsTabPanel.loadIssues,
	// SubmissionIssuesTab.reconcileAgainstLiveDoc) both skip a section whose audit
	// runState==="running", so keeping it there until the fresh
	// [REGULATOR FLAG:…] markers are actually back in the doc stops the
	// ``audit_complete`` issuesRefreshSignal bump (VariantCShell fires it in the
	// same SSE tick, BEFORE this handler) from reconciling the still-stripped doc
	// and auto-resolving every brand-new flag as ``writer_manual``. This is the
	// backend-initiated path (the automatic post-draft review, or a run started on
	// another client), where nothing else sequences the re-projection: without the
	// latch the entry was idle (never attached) or flipped straight to "done"
	// before the un-awaited re-projection landed. Set synchronously — before the
	// first await — so the same-tick reconcile sees "running".
	if (entry.runState !== "running") update(key, { runState: "running" });
	void (async () => {
		let resp: RegulatorFlagsResponse;
		try {
			resp = await getRegulatorFlags(runId, moduleId);
		} catch {
			// Fetch failed — don't strand the entry in the "running" latch above.
			update(key, { runState: priorRunState, progress: null });
			return;
		}
		if (resp.module_id !== moduleId) {
			console.warn(`[reviewStore] module_id mismatch: got ${resp.module_id} want ${moduleId} (sse) — discarded`);
			update(key, { runState: priorRunState, progress: null });
			return;
		}
		const cur = getEntry(key);
		if (cur.ownedByThisClient) return;
		// Record the findings but stay "running" — the flag checker must not run
		// until the updated section is back in the doc (below).
		update(key, {
			...findingsPatch(resp),
			progress: null,
			statusNote: "ok",
			hasRun: true,
			error: null,
		});
		// Re-project the fresh [REGULATOR FLAG: …] markers into the live Word doc
		// on ANY backend-initiated audit_complete — not only when this client saw
		// audit_started first. The auto-audit and the catch-up sweep both fire
		// without this client attaching (often on a terminal run), so gating on
		// "was attached" left the loaded doc showing stale/absent markers until a
		// manual reload. reinsertModuleIntoLiveDoc is a no-op when the doc/heading
		// is absent and defers while the live doc is loading, so this is safe.
		//
		// AWAITED before flipping to "done" (mirrors ``startAudit``): the updated
		// section must land in the doc first, THEN the runState→"done" reconcile —
		// and any reconcile the ``running`` latch was holding off — may run against
		// a doc that actually contains the markers.
		//
		// Custom submissions: a non-owned completion has no ``customSectionNumber``
		// on the entry (only the owning ``startAudit`` sets it), so the reproject
		// would no-op — the module's server ``section_number`` is the synthetic
		// ``CUSTOM-N``, which matches no heading. Resolve the writer's real heading
		// number from the run snapshot (same source as ``loadCustomRunIfApplicable``)
		// and pass it so the markers land at the actual heading. Cheap-gated on the
		// ``CUSTOM-`` id prefix so template runs never pay the extra fetch. This is
		// the server's own annotated draft (not a stale local snapshot), so writing
		// it back is correct. Best-effort — a failure falls back to the no-op.
		let customSectionNumber: string | undefined;
		let customSectionTitle: string | undefined;
		if (moduleId.startsWith("CUSTOM-")) {
			try {
				const snapshot = await getRunSnapshot(runId);
				if (snapshot?.submission_mode === "custom") {
					const row = snapshot.spine?.find((s) => s.id === moduleId);
					customSectionNumber = row?.number || undefined;
					// The title resolves an unnumbered custom heading; the snapshot title
					// can lag a post-import rename (residual risk).
					customSectionTitle = row?.title || undefined;
				}
			} catch (err) {
				console.warn("[reviewStore] custom section-number resolve failed (non-fatal):", err);
			}
		}
		await reprojectSection(runId, moduleId, {
			...(customSectionNumber ? { customSectionNumber } : {}),
			...(customSectionTitle ? { customSectionTitle } : {}),
		});
		if (getEntry(key).ownedByThisClient) return;
		update(key, { runState: "done" as const, error: null });
	})();
}

/** Test hook — drop all entries, listeners, and timers. */
export function __resetForTests(): void {
	for (const key of pollTimers.keys()) stopPoll(key);
	entries.clear();
	listeners.clear();
	auditLibraryBuild.clear();
	auditLibraryBuildListeners.clear();
}
